import React, { useState, useMemo, useCallback } from 'react'
import { useLocation, useNavigate, Link as RouterLink } from 'react-router-dom'
import { generateAdminFpUrl } from '../../utils/urls'
import {
  Button,
  IconButton,
  Chip,
  Stack,
  Avatar,
  Tooltip,
  Tabs,
  Tab,
  Typography,
  Box,
} from '@mui/material'
import {
  PersonAdd as PersonAddIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  AdminPanelSettings as AdminIcon,
  Groups as GroupsIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Clear as ClearIcon,
  DeleteForever as DeleteForeverIcon,
} from '@mui/icons-material'
import { userApi, User } from '../../services/userApi'
import DeleteUserDialog from '../../components/admin/users/DeleteUserDialog'
import ConfirmDialog from '../../components/shared/dialogs/ConfirmDialog'
import { useAuth } from '../../contexts/AuthContext'
import { useNotify } from '../../components/shared/ToastProvider'
import { isAdmin } from '../../services/auth'
import { getProfileImageUrl, getUserInitials } from '../../utils/profileImage'
import RelativeTime from '../../components/shared/RelativeTime'
import AdminPageHeader from '../../components/admin/shared/AdminPageHeader'
import { poolwerxColors } from '../../theme/colors'
import BaseDataTable, { TableColumn } from '../../components/shared/BaseDataTable'
import { useDataFetching } from '../../hooks/useDataFetching'
import { useListPageState } from '../../hooks/useListPageState'
import ReportFilterCard from '../../components/shared/filters/ReportFilterCard'
import { useReportFilters } from '../../components/shared/filters/useReportFilters'
import { FilterKey } from '../../components/shared/filters/types'
import { getRoleColor, getRoleDisplayName } from '../../utils/roleDisplay'

const ENABLED_FILTERS: FilterKey[] = ['textSearch']

const getRoleIcon = (role: string) => {
  switch (role) {
    case 'EVO_ADMIN': return <AdminIcon />
    case 'EVO_TEAM': return <GroupsIcon />
    case 'PSC_TEAM': return <PersonIcon />
    case 'FP_USER': return <BusinessIcon />
    default: return <PersonIcon />
  }
}

const TAB_PATHS = [
  '/admin/users',
  '/admin/users/evo-team',
  '/admin/users/psc-team',
  '/admin/users/fp-users',
] as const

const Users: React.FC = () => {
  const { user: currentUser } = useAuth()
  const { showSuccess, showError } = useNotify()
  const location = useLocation()
  const navigate = useNavigate()
  const activeTab = useMemo(() => {
    const idx = TAB_PATHS.indexOf(location.pathname as typeof TAB_PATHS[number])
    return idx === -1 ? 0 : idx
  }, [location.pathname])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<User | null>(null)
  const [deactivateTarget, setDeactivateTarget] = useState<User | null>(null)
  const [deactivating, setDeactivating] = useState(false)

  // Parse URL parameters for FP filtering
  const fpFilter = useMemo(() => {
    const urlParams = new URLSearchParams(location.search)
    return urlParams.get('fp')
  }, [location.search])

  // Filter state
  const { filters, updateFilter, clearFilters, hasActiveFilters } = useReportFilters({
    enabledFilters: ENABLED_FILTERS,
    syncToUrl: false,
  })

  const { state, updateField } = useListPageState({
    defaultSortField: 'name',
    defaultPerPage: 25
  })

  const fetchUsers = useCallback(async () => {
    const response = await userApi.listUsers()
    return response.data
  }, [])

  const { data: users, loading, error, refetch } = useDataFetching({
    fetchFn: fetchUsers,
    dependencies: [],
    immediate: true
  })

  const filteredUsers = useMemo(() => {
    if (!users) return []

    let filtered = users

    // FP filter from URL
    if (fpFilter) {
      filtered = filtered.filter(user => {
        if (user.franchise_access) {
          return user.franchise_access.some(access => access.fp_code === fpFilter)
        }
        return false
      })
    }

    // Text search filter
    const searchText = (filters.textSearch as string)?.toLowerCase()
    if (searchText) {
      filtered = filtered.filter(user =>
        user.name.toLowerCase().includes(searchText) ||
        user.email.toLowerCase().includes(searchText)
      )
    }

    // Tab filter
    filtered = filtered.filter(user => {
      if (activeTab === 0) return true
      if (activeTab === 1) return user.role === 'EVO_ADMIN' || user.role === 'EVO_TEAM'
      if (activeTab === 2) return user.role === 'PSC_TEAM'
      if (activeTab === 3) return user.role === 'FP_USER'
      return true
    })

    // Sort
    const { sortField, sortDirection } = state
    filtered.sort((a, b) => {
      const aVal = (a as any)[sortField] ?? ''
      const bVal = (b as any)[sortField] ?? ''
      const cmp = typeof aVal === 'string'
        ? aVal.localeCompare(bVal)
        : aVal < bVal ? -1 : aVal > bVal ? 1 : 0
      return sortDirection === 'desc' ? -cmp : cmp
    })

    return filtered
  }, [users, fpFilter, activeTab, filters.textSearch, state.sortField, state.sortDirection])

  const confirmDeactivateUser = async () => {
    if (!deactivateTarget) return
    setDeactivating(true)
    try {
      await userApi.deactivateUser(deactivateTarget.id)
      setDeactivateTarget(null)
      showSuccess('User deactivated')
      refetch()
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to deactivate user')
    } finally {
      setDeactivating(false)
    }
  }

  const handleReactivateUser = async (userId: number) => {
    try {
      await userApi.updateUser(userId, { is_active: true })
      showSuccess('User reactivated')
      refetch()
    } catch (err: any) {
      showError(err.response?.data?.detail || 'Failed to reactivate user')
    }
  }

  const columns: TableColumn<User>[] = [
    {
      key: 'name',
      label: 'User',
      sortable: true,
      render: (user) => (
        <Stack direction="row" alignItems="center" spacing={2}>
          <Avatar
            src={getProfileImageUrl(user)}
            sx={{ width: 32, height: 32 }}
            slotProps={{
              img: {
                onError: (e: any) => {
                  e.currentTarget.style.display = 'none'
                }
              }
            }}
          >
            {getUserInitials(user)}
          </Avatar>
          <Typography fontWeight={500}>{user.name}</Typography>
        </Stack>
      )
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (user) => (
        <Chip
          icon={getRoleIcon(user.role)}
          label={getRoleDisplayName(user.role)}
          color={getRoleColor(user.role)}
          size="small"
        />
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (user) => (
        user.is_active ? (
          <Chip
            icon={<CheckCircleIcon />}
            label="Active"
            color="success"
            size="small"
            variant="outlined"
          />
        ) : (
          <Chip
            icon={<BlockIcon />}
            label="Inactive"
            color="error"
            size="small"
            variant="outlined"
          />
        )
      )
    },
    {
      key: 'fp_access',
      label: 'FP Access',
      sortable: false,
      render: (user) => {
        if (user.franchise_access && user.franchise_access.length > 0) {
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {user.franchise_access.slice(0, 3).map(access => (
                <Chip
                  key={access.fp_code}
                  label={access.fp_code}
                  size="small"
                  variant="outlined"
                  component={RouterLink}
                  to={generateAdminFpUrl(access.fp_id, 'edit?tab=4')}
                  clickable
                />
              ))}
              {user.franchise_access.length > 3 && (
                <Chip
                  label={`+${user.franchise_access.length - 3}`}
                  size="small"
                  variant="outlined"
                />
              )}
            </Stack>
          )
        } else if (user.role === 'FP_USER') {
          return (
            <Typography variant="body2" color="text.secondary">
              No access
            </Typography>
          )
        } else if (user.allowed_markets && user.allowed_markets.length > 0) {
          return (
            <Typography variant="body2">
              {user.allowed_markets.join(', ')}
            </Typography>
          )
        }
        return (
          <Typography variant="body2" color="text.secondary">
            All FPs
          </Typography>
        )
      }
    },
    {
      key: 'last_login',
      label: 'Last Login',
      sortable: true,
      render: (user) => <RelativeTime value={user.last_login} />
    },
    {
      key: 'last_seen_at',
      label: 'Last Active',
      sortable: true,
      render: (user) => <RelativeTime value={user.last_seen_at} />
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      render: (user) => (
        isAdmin(currentUser) && user.id !== currentUser?.id ? (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Tooltip title="Edit">
              <IconButton
                size="small"
                component={RouterLink}
                to={`/admin/users/${user.id}/edit`}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {user.is_active && (
              <Tooltip title="Deactivate">
                <IconButton
                  size="small"
                  onClick={() => setDeactivateTarget(user)}
                  color="error"
                >
                  <BlockIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!user.is_active && (
              <Tooltip title="Reactivate">
                <IconButton
                  size="small"
                  onClick={() => handleReactivateUser(user.id)}
                  color="success"
                >
                  <CheckCircleIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!user.is_active && user.role !== 'EVO_ADMIN' && (
              <Tooltip title="Delete permanently">
                <IconButton
                  size="small"
                  onClick={() => {
                    setUserToDelete(user)
                    setDeleteDialogOpen(true)
                  }}
                  color="error"
                >
                  <DeleteForeverIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        ) : null
      )
    }
  ]

  return (
    <>
      <AdminPageHeader
        pageKey="userManagement"
        actions={
          isAdmin(currentUser) ? (
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              component={RouterLink}
              to="/admin/users/new"
              sx={{
                backgroundColor: poolwerxColors.teal,
                '&:hover': { backgroundColor: poolwerxColors.tealHover },
              }}
            >
              Add User
            </Button>
          ) : undefined
        }
      />

      {/* FP Filter Info */}
      {fpFilter && (
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body1" color="text.secondary">
              Showing users for: <strong>{fpFilter.toUpperCase()}</strong>
            </Typography>
            <Button
              size="small"
              startIcon={<ClearIcon />}
              component={RouterLink}
              to="/admin/users"
              sx={{ color: '#e91e63' }}
            >
              Clear Filter
            </Button>
          </Stack>
        </Box>
      )}

      {/* Search Filter */}
      <ReportFilterCard
        filters={filters}
        onFilterChange={updateFilter}
        onClearFilters={clearFilters}
        enabledFilters={ENABLED_FILTERS}
        hasActiveFilters={hasActiveFilters}
      />

      {/* User Role Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => navigate(TAB_PATHS[v] + location.search)}
        sx={{ mb: 3 }}
      >
        <Tab label={`All Users (${users?.length || 0})`} />
        <Tab label={`Evolution Team (${users?.filter(u => u.role === 'EVO_ADMIN' || u.role === 'EVO_TEAM').length || 0})`} />
        <Tab label={`PSC Team (${users?.filter(u => u.role === 'PSC_TEAM').length || 0})`} />
        <Tab label={`FP Users (${users?.filter(u => u.role === 'FP_USER').length || 0})`} />
      </Tabs>

      {/* Users Table */}
      <BaseDataTable
        columns={columns}
        data={filteredUsers}
        loading={loading}
        error={error}
        sortField={state.sortField}
        sortDirection={state.sortDirection}
        onSort={(field, direction) => {
          updateField('sortField', field)
          updateField('sortDirection', direction)
        }}
        emptyMessage="No users found"
        hover={true}
        getRowKey={(user) => user.id}
      />

      {/* Delete User Dialog */}
      <DeleteUserDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false)
          setUserToDelete(null)
        }}
        user={userToDelete}
        onSuccess={(message) => {
          showSuccess(message)
          refetch()
        }}
        onError={(message) => {
          showError(message)
        }}
      />

      {/* Deactivate User Dialog */}
      <ConfirmDialog
        open={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={confirmDeactivateUser}
        tone="warning"
        title="Deactivate User"
        pending={deactivating}
        confirmLabel="Deactivate"
        body={
          <Typography variant="body2">
            Are you sure you want to deactivate <strong>{deactivateTarget?.name}</strong>
            {deactivateTarget?.email ? ` (${deactivateTarget.email})` : ''}? They can be
            reactivated later.
          </Typography>
        }
      />
    </>
  )
}

export default Users
